export type Breakpoint = 'mobile' | 'desktop';
export type AnchorPoint = 'top' | 'right' | 'bottom' | 'left' | 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type PathType = 'straight' | 'curved' | 'stepped';

/**
 * How a line animates in:
 * - load:    draws once on mount after `animation.delay` (good above the fold)
 * - in-view: draws once when its watched element scrolls into view; `animation.delay`
 *            acts as the earliest start after mount so above-the-fold lines can wait
 *            for the spine to reach them
 * - scroll:  path length is bound to scroll progress; a glowing tip rides the end
 */
export type Trigger = 'load' | 'in-view' | 'scroll';
export type NodeEnd = 'start' | 'end' | 'both';

export interface Point {
  x: number;
  y: number;
}

/** rootMargin shared by in-view lines and in-view cards so they fire together. */
export const IN_VIEW_MARGIN = '0px 0px -38% 0px';

export interface LineStyle {
  color: string;
  width: number;
  opacity: number;
  dashArray?: string;
  nodeColor: string;
  tipColor: string;
  glowColor: string;
}

export interface CalculatedLine {
  id: string;
  /** Document coordinates, at least two points. */
  points: Point[];
  path: PathType;
  trigger: Trigger;
  style: LineStyle;
  animation: {
    delay: number;
    duration: number;
    ease: string;
  };
  node?: NodeEnd;
  inView: { watch?: string; margin: string };
  scroll: { focus: number };
  reveals?: string;
}

export interface ElementTarget {
  element: string; // CSS selector
  anchor?: AnchorPoint;
  offset?: { x?: number; y?: number }; // Optional pixel offset from anchor
}

export interface ViewportTarget {
  x: string; // e.g. "50%", "100px"
  y: string;
}

// Computed target - returns a position (or null if elements aren't ready)
export type TargetFn = () => Point | null;

export type Target = string | ElementTarget | ViewportTarget | TargetFn;

export interface Connection {
  id?: string;
  from: Target;
  to: Target;
  /** Optional waypoints between `from` and `to` (drawn as a polyline). */
  via?: Target[];
  breakpoints?: Breakpoint[]; // Which breakpoints to show on. Defaults to all
  path?: PathType; // Defaults to 'straight'
  trigger?: Trigger; // Defaults to 'load'
  /** Draw a timeline node dot at one or both ends. */
  node?: NodeEnd;
  /** in-view trigger options. `watch` defaults to the `to` (then `from`) element selector. */
  inView?: { watch?: string; margin?: string };
  /** scroll trigger options. `focus` is the viewport fraction the tip sits at (0..1). */
  scroll?: { focus?: number };
  /** Reveal-store key marked when this line starts drawing. Defaults to `id`. */
  reveals?: string;
  style?: {
    color?: string;
    width?: number;
    opacity?: number;
    dashArray?: string; // e.g. "5,5" for dashed line
    nodeColor?: string;
    tipColor?: string;
    glowColor?: string;
  };
  animation?: {
    delay?: number;
    duration?: number;
    ease?: string;
  };
}

// Helper to determine if target is a computed target
export function isTargetFn(target: Target): target is TargetFn {
  return typeof target === 'function';
}

// Helper to determine if target is a viewport target
export function isViewportTarget(target: Target): target is ViewportTarget {
  return typeof target === 'object' && 'x' in target && 'y' in target;
}

// Helper to determine if target is an element target
export function isElementTarget(target: Target): target is ElementTarget {
  return typeof target === 'object' && 'element' in target;
}

// Helper to normalize target to ElementTarget
export function normalizeTarget(target: Target): ElementTarget | ViewportTarget | TargetFn {
  if (typeof target === 'string') {
    return { element: target, anchor: 'center' };
  }
  if (isTargetFn(target) || isViewportTarget(target)) {
    return target;
  }
  return {
    ...target,
    anchor: target.anchor || 'center',
  };
}

// Get anchor position relative to element bounds
export function getAnchorPosition(
  bounds: DOMRect,
  anchor: AnchorPoint = 'center'
): Point {
  const positions = {
    'top': { x: bounds.left + bounds.width / 2, y: bounds.top },
    'right': { x: bounds.right, y: bounds.top + bounds.height / 2 },
    'bottom': { x: bounds.left + bounds.width / 2, y: bounds.bottom },
    'left': { x: bounds.left, y: bounds.top + bounds.height / 2 },
    'center': { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 },
    'top-left': { x: bounds.left, y: bounds.top },
    'top-right': { x: bounds.right, y: bounds.top },
    'bottom-left': { x: bounds.left, y: bounds.bottom },
    'bottom-right': { x: bounds.right, y: bounds.bottom },
  };
  return positions[anchor];
}

/**
 * Viewport-relative rect of an element from its *layout* geometry (offset
 * chain), ignoring CSS transforms. Entrance animations translate elements, so
 * getBoundingClientRect() measured mid-animation would put lines in the wrong
 * place; layout geometry is stable from first paint.
 */
export function measure(el: Element): DOMRect {
  if (!(el instanceof HTMLElement)) return el.getBoundingClientRect();
  let top = 0;
  let left = 0;
  let node: HTMLElement | null = el;
  while (node) {
    top += node.offsetTop;
    left += node.offsetLeft;
    const parent = node.offsetParent as HTMLElement | null;
    if (parent) {
      top += parent.clientTop;
      left += parent.clientLeft;
    }
    node = parent;
  }
  // The chain ends relative to <body>'s padding edge; rebase to the viewport.
  const body = document.body.getBoundingClientRect();
  return new DOMRect(
    left + body.left + document.body.clientLeft,
    top + body.top + document.body.clientTop,
    el.offsetWidth,
    el.offsetHeight
  );
}

/**
 * Viewport-relative anchor of the first element matching `selector`,
 * or null if it isn't in the DOM yet. Handy inside TargetFns.
 */
export function anchorOf(selector: string, anchor: AnchorPoint = 'center'): Point | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  return getAnchorPosition(measure(el), anchor);
}

// Convert viewport percentage to pixels
export function parseViewportValue(value: string, dimension: 'width' | 'height'): number {
  if (value.includes('%')) {
    const percent = parseFloat(value) / 100;
    return dimension === 'width'
      ? window.innerWidth * percent
      : window.innerHeight * percent;
  }
  return parseFloat(value);
}
